// Profile photo upload/read helpers.
//
// The dashboard app also lets users set a profile picture; both apps share the
// same convention so either can write it and both can display it:
//   - Firestore:  users/{uid}.photoURL  (download URL string)
//   - Storage:    profilePhotos/{uid}/avatar_<ts>.jpg
//
// Uploads are center-cropped to a square and downscaled to a small JPEG so the
// avatar stays tiny (fast to load, cheap to store).

import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db, storage } from "../firebase";
import { authedWrite } from "../utils/authedWrite";

const AVATAR_MAX_DIM = 256;
const AVATAR_QUALITY = 0.85;

/** Read the profile photo URL from a user profile, tolerating field variants. */
export function getProfilePhotoUrl(profile) {
  if (!profile || typeof profile !== "object") return "";
  return profile.photoURL || profile.photoUrl || profile.profilePhotoUrl || "";
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read the selected file."));
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("That image couldn't be loaded."));
    img.src = src;
  });
}

// Center-crop to a square, scale down to a small JPEG blob.
async function toSquareJpegBlob(file, maxDim = AVATAR_MAX_DIM, quality = AVATAR_QUALITY) {
  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImage(dataUrl);
  const { width, height } = img;
  if (!width || !height) throw new Error("That image couldn't be read.");

  const side = Math.min(width, height);
  const sx = Math.round((width - side) / 2);
  const sy = Math.round((height - side) / 2);
  const target = Math.min(maxDim, side);

  const canvas = document.createElement("canvas");
  canvas.width = target;
  canvas.height = target;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, target, target);
  ctx.drawImage(img, sx, sy, side, side, 0, 0, target, target);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Could not process the image."))),
      "image/jpeg",
      quality
    );
  });
}

/**
 * Upload a new profile photo for the signed-in user and write its URL to the
 * user doc. Returns the download URL.
 */
export async function uploadProfilePhoto(file) {
  const user = auth.currentUser;
  if (!user) throw new Error("You must be signed in to update your photo.");
  if (!file || !String(file.type || "").startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }

  const blob = await toSquareJpegBlob(file);
  const path = `profilePhotos/${user.uid}/avatar_${Date.now()}.jpg`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, { contentType: "image/jpeg" });
  const url = await getDownloadURL(storageRef);

  await authedWrite(
    () =>
      setDoc(
        doc(db, "users", user.uid),
        { photoURL: url, photoUpdatedAt: serverTimestamp() },
        { merge: true }
      ),
    "profile-photo-set"
  );

  return url;
}

/** Clear the signed-in user's profile photo (leaves the storage object; the URL is cleared). */
export async function removeProfilePhoto() {
  const user = auth.currentUser;
  if (!user) throw new Error("You must be signed in to update your photo.");
  await authedWrite(
    () =>
      setDoc(
        doc(db, "users", user.uid),
        { photoURL: "", photoUpdatedAt: serverTimestamp() },
        { merge: true }
      ),
    "profile-photo-remove"
  );
}
